/**
 * Push notification setup for Capacitor (iOS / Android native) and a no-op
 * stub on web. Wires Capacitor's PushNotifications plugin to:
 *   1. Request permission on first launch (after sign-in).
 *   2. Register with FCM/APNs and POST the token to `/api/push/register`.
 *   3. Route incoming taps to the deep link from payload.data.link.
 */
import { Capacitor } from "@capacitor/core";
import { api, getUser } from "./api";

let initialised = false;

/**
 * Register the device for push notifications and persist its token on the
 * backend. Safe to call multiple times — guards against re-init.
 * On web (PWA), this is a no-op for now; web push (VAPID) is a P2 backlog item.
 */
export async function ensurePushRegistration({ navigate } = {}) {
  if (initialised) return;
  if (!Capacitor.isNativePlatform()) return; // web — handled by WS + bell only
  // Android push requires Firebase Cloud Messaging (google-services.json +
  // FirebaseApp init). Until we ship a Firebase project for the Play build,
  // calling PushNotifications.register() on Android throws a fatal
  // IllegalStateException from FirebaseApp.getInstance(). iOS uses APNs
  // directly via the aps-environment entitlement and is unaffected.
  if (Capacitor.getPlatform() === "android") return;
  const user = getUser();
  if (!user) return;

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.checkPermissions();
    let status = perm.receive;
    if (status === "prompt" || status === "prompt-with-rationale") {
      const r = await PushNotifications.requestPermissions();
      status = r.receive;
    }
    if (status !== "granted") return;

    await PushNotifications.register();

    PushNotifications.addListener("registration", async (tokenInfo) => {
      try {
        await api.post(
          "/push/register",
          { token: tokenInfo.value, platform: Capacitor.getPlatform() },
          { params: { user_id: user.id } }
        );
      } catch (e) {
        // ignore — server may be offline
      }
    });

    PushNotifications.addListener("registrationError", (err) => {
      // eslint-disable-next-line no-console
      console.warn("Push registration error", err);
    });

    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const link = action?.notification?.data?.link;
      if (link && navigate) {
        try {
          navigate(link);
        } catch {
          window.location.assign(link);
        }
      }
    });

    initialised = true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Push setup failed", e);
  }
}
